/**
 * Recover browsers that still hold a previous deployment's webpack runtime.
 *
 * Hashed chunks are cached as immutable for a year. After a deploy, an open
 * tab or a cached document keeps the old `webpack-*.js` runtime, while the
 * App Router then loads chunks from the new build into that same
 * `webpackChunk_N_E` runtime. `__webpack_require__` throws because the module
 * factory is missing:
 *
 * - Chrome: Cannot read properties of undefined (reading 'call')
 * - Firefox: can't access property "call", l[e] is undefined
 * - Safari: l[e] is not a function / 'l[e]' is undefined
 *
 * The throw site is the webpack runtime itself (`l[e].call(...)`), not
 * application code. A generic `.call` TypeError whose first stack frame is an
 * application chunk must still be reported.
 */

export const DEPLOYMENT_RELOAD_QUERY_PARAM = "__dpl";
export const DEPLOYMENT_RELOAD_STORAGE_KEY =
  "reputech:deployment-reload-target";
export const STALE_ASSET_RELOAD_MARKER = "stale-assets";

const WEBPACK_RUNTIME_FILE = /webpack-[a-f0-9]+\.js/;
const WEBPACK_RUNTIME_URL = /\/_next\/static\/chunks\/webpack-[a-f0-9]+\.js/;

export type DeploymentStorage = Pick<Storage, "getItem" | "setItem">;

export type DeploymentLocation = {
  href: string;
  replace: (url: string) => void;
};

export function isUnambiguousMinifiedWebpackMessage(message: string): boolean {
  return (
    message.includes("l[e]") &&
    (message.includes("not a function") ||
      message.includes("is undefined") ||
      message.includes(".call") ||
      message.includes('property "call"') ||
      message.includes("property 'call'"))
  );
}

export function isGenericMissingCallMessage(message: string): boolean {
  return (
    (message.includes("Cannot read properties of undefined") &&
      message.includes("reading 'call'")) ||
    (message.includes("undefined is not an object") &&
      message.includes(".call")) ||
    (message.includes("can't access property") && message.includes("call"))
  );
}

/**
 * True when the error was thrown from the webpack runtime file.
 * Later frames often mention that file for ordinary module errors, so only
 * the first stack frame (or a bare filename) counts.
 */
export function isThrownFromWebpackRuntime(source: string): boolean {
  const lines = source
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const firstFrame = lines.find(
    (line) => line.startsWith("at ") || /@.+\.js/.test(line),
  );

  if (firstFrame) {
    return WEBPACK_RUNTIME_FILE.test(firstFrame);
  }

  return lines.some((line) => WEBPACK_RUNTIME_URL.test(line));
}

export function isStaleWebpackRuntimeError(
  message: string,
  source = "",
): boolean {
  if (!message) {
    return false;
  }

  if (isUnambiguousMinifiedWebpackMessage(message)) {
    return true;
  }

  if (!isGenericMissingCallMessage(message)) {
    return false;
  }

  return isThrownFromWebpackRuntime(source);
}

export function isChunkLoadFailureMessage(message: string): boolean {
  return (
    (message.includes("Loading chunk") ||
      message.includes("Loading CSS chunk")) &&
    message.includes("failed")
  );
}

export function shouldRecoverFromStaleAssets(
  message: string,
  source = "",
): boolean {
  return (
    isStaleWebpackRuntimeError(message, source) ||
    isChunkLoadFailureMessage(message)
  );
}

export function readDeploymentReloadMarker(
  storage: Pick<Storage, "getItem"> | null | undefined,
): string | null {
  try {
    return storage?.getItem(DEPLOYMENT_RELOAD_STORAGE_KEY) ?? null;
  } catch {
    // Unreadable storage cannot record a reload, so callers must not navigate.
    return STALE_ASSET_RELOAD_MARKER;
  }
}

/**
 * Returns the marker to store when the running bundle is older than the
 * server. A marker equal to the server version means that reload already
 * happened, so a cached document cannot loop.
 */
export function planDeploymentReload(
  clientVersion: string | null | undefined,
  serverVersion: string | null | undefined,
  storedMarker: string | null,
): { marker: string } | null {
  const client = clientVersion?.trim() ?? "";
  const server = serverVersion?.trim() ?? "";

  if (!client || !server || client === server) {
    return null;
  }

  if (storedMarker === server) {
    return null;
  }

  return { marker: server };
}

export function reloadForFreshDeployment(
  locationLike: DeploymentLocation,
  storage: DeploymentStorage | null | undefined,
  marker: string,
): boolean {
  const reloadMarker = marker.trim();

  if (!reloadMarker || !storage) {
    return false;
  }

  try {
    if (storage.getItem(DEPLOYMENT_RELOAD_STORAGE_KEY) === reloadMarker) {
      return false;
    }

    storage.setItem(DEPLOYMENT_RELOAD_STORAGE_KEY, reloadMarker);
  } catch {
    return false;
  }

  const url = new URL(locationLike.href);
  url.searchParams.set(DEPLOYMENT_RELOAD_QUERY_PARAM, reloadMarker);
  locationLike.replace(url.toString());
  return true;
}

export function stripDeploymentReloadParam(href: string): string | null {
  const url = new URL(href);

  if (!url.searchParams.has(DEPLOYMENT_RELOAD_QUERY_PARAM)) {
    return null;
  }

  url.searchParams.delete(DEPLOYMENT_RELOAD_QUERY_PARAM);
  const query = url.searchParams.toString();

  return `${url.pathname}${query ? `?${query}` : ""}${url.hash}`;
}

export const STRIP_DEPLOYMENT_RELOAD_PARAM_SCRIPT = `(function(){try{var p=${JSON.stringify(DEPLOYMENT_RELOAD_QUERY_PARAM)};var u=new URL(location.href);if(!u.searchParams.has(p))return;u.searchParams.delete(p);var q=u.searchParams.toString();history.replaceState(history.state,"",u.pathname+(q?"?"+q:"")+u.hash);}catch(e){}})();`;

type RecoveryErrorEvent = {
  message?: string;
  filename?: string;
  error?: { message?: string; stack?: string } | null;
  reason?: unknown;
  preventDefault: () => void;
  stopImmediatePropagation: () => void;
};

type RecoveryWindow = {
  addEventListener: (
    type: string,
    listener: (event: RecoveryErrorEvent) => void,
    capture?: boolean,
  ) => void;
  removeEventListener: (
    type: string,
    listener: (event: RecoveryErrorEvent) => void,
    capture?: boolean,
  ) => void;
  location: DeploymentLocation;
  sessionStorage?: DeploymentStorage;
};

const installedWindows = new WeakSet<object>();

function messageFromUnknown(value: unknown): string {
  if (value instanceof Error) {
    return value.message;
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
}

function sourceFromUnknown(value: unknown): string {
  return value instanceof Error ? value.stack || "" : "";
}

function recoverStaleAssetEvent(
  event: RecoveryErrorEvent,
  target: RecoveryWindow,
  message: string,
  source: string,
): void {
  if (!shouldRecoverFromStaleAssets(message, source)) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  reloadForFreshDeployment(
    target.location,
    target.sessionStorage,
    STALE_ASSET_RELOAD_MARKER,
  );
}

export function installStaleDeploymentRecovery(target: RecoveryWindow): void {
  if (installedWindows.has(target)) {
    return;
  }

  installedWindows.add(target);

  target.addEventListener(
    "error",
    (event) => {
      const message = event.message || messageFromUnknown(event.error);
      const source = `${event.filename || ""}\n${sourceFromUnknown(event.error)}`;
      recoverStaleAssetEvent(event, target, message, source);
    },
    true,
  );

  target.addEventListener(
    "unhandledrejection",
    (event) => {
      const message = messageFromUnknown(event.reason);
      const source = sourceFromUnknown(event.reason);
      recoverStaleAssetEvent(event, target, message, source);
    },
    true,
  );
}
